/* eslint-disable import/no-extraneous-dependencies */
import { FormLabelAlign } from 'antd/lib/form/interface';
import { useField } from 'formik';
import { Rule } from 'rc-field-form/lib/interface';
import React, { CSSProperties, ReactNode, useEffect, useMemo, useState } from 'react';
import { InputNumber } from 'antd';
// import CurrencyInput from 'react-currency-input-field';
import { FormItem } from 'formik-antd';
import { isUndefinedOrNullOrEmpty } from 'common';
import { useTranslation } from 'react-i18next';

export interface CurrencyNumberInputProp {
  value?: string | number;
  id: string;
  name: string;
  className?: string;
  label: string;
  extra?: string;
  placeholder?: string;
  prefix?: string;
  suffix?: string;
  labelAlign?: FormLabelAlign; // default: right
  labelCol?: object; // ant design Col object
  wrapperCol?: object; // ant design Col object
  tooltip?: ReactNode;
  rules?: Rule[];
  noStyle?: boolean;
  required?: boolean;
  minLimit?: number;
  maxLimit?: number;
  disableAbbreviations?: boolean;
  disableGroupSeparators?: boolean;
  allowDecimal?: boolean;
  allowNegativeValue?: boolean;
  decimalScale?: number;
  decimalsLimit?: number;
  disabled?: boolean;
  readOnly?: boolean;
  maxLength?: number;
  inputStyle?: CSSProperties;
  handleChangeInput?: (name: string, value: number | string, formatted: string, errorMessages?: string) => void;
}

export const CurrencyNumberInput: React.FC<CurrencyNumberInputProp> = ({
  className,
  value,
  name,
  minLimit,
  prefix,
  suffix,
  maxLimit,
  handleChangeInput,
  label,
  extra,
  tooltip,
  labelCol,
  wrapperCol,
  labelAlign,
  required,
  rules,
  noStyle,
  id,
  placeholder,
  disableAbbreviations,
  disableGroupSeparators,
  allowNegativeValue,
  allowDecimal,
  decimalScale,
  decimalsLimit,
  disabled,
  readOnly,
  maxLength,
  inputStyle,
}) => {
  const { t } = useTranslation();
  const [field, meta, helper] = useField({ name });
  const [errorMessages, setErrorMessage] = useState('');
  const [classNameState, setClassNameState] = useState('');

  // const handleOnValueChange = (
  //   valueData?: string | number,
  //   nameVal?: string,
  //   values?: {
  //     float: number | null;
  //     formatted: string | null;
  //     value: string | null;
  //   }
  // ): void => {
  //   let errorFlag = false;
  //
  //   if (Number.isNaN(Number(valueData))) {
  //     setErrorMessage(t('validate.requiredValidNumber'));
  //     errorFlag = true;
  //   }
  //
  //   if (minLimit !== undefined && minLimit > Number(valueData)) {
  //     setErrorMessage(`value must at least ${prefix ?? ''}${minLimit}${suffix ?? ''}`);
  //     errorFlag = true;
  //   }
  //
  //   if (maxLimit !== undefined && Number(valueData) > maxLimit) {
  //     setErrorMessage(`value must at less than or equal to ${prefix ?? ''}${maxLimit}${suffix ?? ''}`);
  //     // eslint-disable-next-line no-param-reassign
  //     valueData = maxLimit;
  //     errorFlag = true;
  //   }
  //
  //   if (errorFlag) {
  //     setClassNameState('ant-form-item-has-error');
  //   } else {
  //     setClassNameState('');
  //     setErrorMessage('');
  //   }
  //
  //   helper.setValue(valueData);
  //
  //   if (handleChangeInput && nameVal != null) {
  //     handleChangeInput(nameVal, valueData ?? '', values?.formatted ?? '', errorMessages);
  //   }
  // };

  const handleOnChange = (valueData: number | string | null): void => {
    let errorFlag = false;
    let val: number | string | undefined = valueData ?? undefined;

    if (val !== undefined && Number.isNaN(Number(val))) {
      setErrorMessage(t('validate.requiredValidNumber'));
      errorFlag = true;
    }

    if (minLimit !== undefined && minLimit > Number(val)) {
      setErrorMessage(`value must at least ${prefix ?? ''}${minLimit}${suffix ?? ''}`);
      errorFlag = true;
    }

    if (maxLimit !== undefined && Number(val) > maxLimit) {
      setErrorMessage(`value must at less than or equal to ${prefix ?? ''}${maxLimit}${suffix ?? ''}`);
      val = maxLimit;
      errorFlag = true;
    }

    if (errorFlag) {
      setClassNameState('antd-v5-form-item-has-error');
    } else {
      setClassNameState('');
      setErrorMessage('');
    }

    helper.setValue(val);

    if (handleChangeInput) {
      const formatted = val !== undefined ? `${prefix ?? ''}${val}${suffix ?? ''}` : '';
      handleChangeInput(name, val ?? '', formatted, errorMessages);
    }
  };

  const precision = useMemo(() => {
    if (allowDecimal === false) return 0;
    return decimalScale ?? decimalsLimit ?? undefined;
  }, [allowDecimal, decimalScale, decimalsLimit]);

  const formatter = useMemo(() => {
    if (disableGroupSeparators) return undefined;
    return (val: number | string | undefined) => {
      if (val === undefined || val === '') return '';
      const parts = `${val}`.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    };
  }, [disableGroupSeparators]);

  const parser = useMemo(() => {
    if (disableGroupSeparators) return undefined;
    return (val: string | undefined) => val?.replace(/,/g, '') as any;
  }, [disableGroupSeparators]);

  const handleOnBlur = (event: any): void => {
    if (!isUndefinedOrNullOrEmpty(event.target.value) && !Number.isNaN(Number(event.target.value))) {
      helper.setValue(Number(event.target.value) || undefined);
    }
  };

  const displayValidateStatus = useMemo(() => {
    if (meta.touched && (meta.error || errorMessages)) return 'error';

    return '';
  }, [errorMessages, meta.error, meta.touched]);

  useEffect(() => {
    if (errorMessages) {
      helper.setTouched(true, true);
      helper.setError(errorMessages);
    } else {
      helper.setError(undefined);
    }
  }, [errorMessages, helper]);

  return (
    <FormItem
      label={label}
      name={field.name}
      className={`${className} ${classNameState}`}
      tooltip={tooltip}
      labelCol={labelCol}
      wrapperCol={wrapperCol}
      help={meta.touched && (meta.error || errorMessages) ? errorMessages || t(`${meta.error}`) : undefined}
      labelAlign={labelAlign}
      required={required}
      rules={rules}
      noStyle={noStyle}
      validateStatus={displayValidateStatus}
      extra={extra}
    >
      {/* <CurrencyInput
        id={id}
        name={name}
        className={`antd-v5-input-affix-wrapper ${invalidErrorInputClassName}`}
        value={field.value || value}
        onValueChange={handleOnValueChange}
        onBlur={handleOnBlur}
        placeholder={placeholder}
        prefix={prefix}
        suffix={suffix}
        disableAbbreviations={disableAbbreviations}
        disableGroupSeparators={disableGroupSeparators}
        allowNegativeValue={allowNegativeValue}
        allowDecimals={allowDecimal}
        step={1}
        maxLength={maxLength}
        decimalScale={decimalScale}
        decimalsLimit={decimalsLimit}
        disabled={disabled}
        readOnly={readOnly}
        style={inputStyle}
      /> */}
      <div>
        <InputNumber
          id={id}
          name={name}
          value={field.value ?? value ?? null}
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          placeholder={placeholder}
          prefix={prefix}
          suffix={suffix}
          // min={allowNegativeValue === false ? 0 : undefined}
          // max={maxLimit}
          step={1}
          maxLength={maxLength}
          precision={precision}
          disabled={disabled}
          readOnly={readOnly}
          style={{ width: '100%', ...inputStyle }}
          controls={false}
          keyboard={!disableAbbreviations}
          formatter={formatter}
          parser={parser}
          status={displayValidateStatus === 'error' ? 'error' : undefined}
        />
      </div>
    </FormItem>
  );
};

CurrencyNumberInput.defaultProps = {
  labelAlign: 'left',
};
